import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Download, Filter, Calendar, BarChart3, 
  TrendingUp, TrendingDown, DollarSign, Package, Truck
} from 'lucide-react';

const ReportsTab = ({ timeRange }) => {
  const [selectedReport, setSelectedReport] = useState('overview');

  const reports = [
    {
      id: 'overview',
      name: 'Executive Overview',
      description: 'High-level performance metrics and KPIs',
      icon: BarChart3,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'logistics',
      name: 'Logistics Report',
      description: 'Transportation and delivery performance',
      icon: Truck,
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'inventory',
      name: 'Inventory Analysis',
      description: 'Stock levels and turnover analysis',
      icon: Package,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'financial',
      name: 'Financial Summary',
      description: 'Cost analysis and revenue metrics',
      icon: DollarSign,
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="p-8 space-y-8">
      {/* Reports Header */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-gray-600 to-slate-600 rounded-3xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Reports & Analytics</h2>
            <p className="text-gray-100">Comprehensive reporting and data export</p>
          </div>
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300 flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export All</span>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Report Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {reports.map((report, index) => (
          <motion.div
            key={report.id}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            onClick={() => setSelectedReport(report.id)}
            className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
              selectedReport === report.id
                ? 'bg-white/80 shadow-xl border-2 border-blue-500'
                : 'bg-white/60 backdrop-blur-sm shadow-xl border border-white/20 hover:bg-white/80'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${report.color} flex items-center justify-center mb-4`}>
              <report.icon className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{report.name}</h3>
            <p className="text-gray-600 text-sm mb-4">{report.description}</p>
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              Generate Report →
            </button>
          </motion.div>
        ))}
      </div>

      {/* Report Content */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {reports.find(r => r.id === selectedReport)?.name}
            </h3>
            <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export PDF</span>
            </button>
            <button className="px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export Excel</span>
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Report content based on selection */}
          {selectedReport === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { label: 'Total Revenue', value: 'SAR 2.4M', change: '+15.2%', trend: 'up' },
                { label: 'Order Fulfillment', value: '94.8%', change: '+2.1%', trend: 'up' },
                { label: 'Customer Satisfaction', value: '4.6/5', change: '+0.2', trend: 'up' }
              ].map((metric, index) => (
                <div key={metric.label} className="p-4 bg-white/50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">{metric.label}</span>
                    <div className={`flex items-center text-sm ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                      {metric.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {metric.change}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                </div>
              ))}
            </div>
          )}

          {selectedReport === 'logistics' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/50 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Delivery Performance</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">On-time Delivery</span>
                      <span className="text-sm font-medium">94.2%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Average Transit Time</span>
                      <span className="text-sm font-medium">2.3 days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Active Shipments</span>
                      <span className="text-sm font-medium">89</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white/50 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Cost Analysis</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Transportation Cost</span>
                      <span className="text-sm font-medium">SAR 45K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Fuel Cost</span>
                      <span className="text-sm font-medium">SAR 12K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Maintenance Cost</span>
                      <span className="text-sm font-medium">SAR 8K</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedReport === 'inventory' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/50 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Stock Levels</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Items</span>
                      <span className="text-sm font-medium">5,295</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Low Stock Items</span>
                      <span className="text-sm font-medium">23</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Out of Stock</span>
                      <span className="text-sm font-medium">5</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white/50 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Turnover Analysis</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Average Turnover</span>
                      <span className="text-sm font-medium">12.5x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Slow Moving Items</span>
                      <span className="text-sm font-medium">156</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Fast Moving Items</span>
                      <span className="text-sm font-medium">892</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedReport === 'financial' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/50 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Revenue Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Total Revenue</span>
                      <span className="text-sm font-medium">SAR 2.4M</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Cost of Goods</span>
                      <span className="text-sm font-medium">SAR 1.6M</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Gross Margin</span>
                      <span className="text-sm font-medium">33.3%</span>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white/50 rounded-xl border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Operational Costs</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Logistics Cost</span>
                      <span className="text-sm font-medium">SAR 180K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Warehouse Cost</span>
                      <span className="text-sm font-medium">SAR 95K</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Technology Cost</span>
                      <span className="text-sm font-medium">SAR 45K</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ReportsTab; 